import React, { useEffect, useState }  from 'react'
import { View, StyleSheet, FlatList } from 'react-native'
import StyledTextInput from './StyledTextInput'
import { Icon, Text, Button, Spinner, useTheme } from '@ui-kitten/components'
import * as Sentry from "@sentry/react-native"
import { useTranslation } from 'react-i18next'
import { useAtomValue, useSetAtom } from 'jotai'
import { updateConstantsData } from '../global/Firebase/firebase'
import { assignMissingConstantIds } from './constantsItemId'

const getInitialData = (initialData, initialDataPath) => {
  let d = initialData
  initialDataPath.forEach((element) => {
    d = d[element]
  })
  return d
}

export default function EditableListScreen({ navigation, route }) {
  const { atom, initialDataPath, title, ref, headerComponent } = route.params
  const initialData = useAtomValue(atom)
  const setAtomData = useSetAtom(atom)
  const loadedData = initialDataPath ? getInitialData(initialData, initialDataPath) : Object.values(initialData)
  const [data, setData] = useState(loadedData)
  // Store original data for Supabase sync comparison
  const [originalData] = useState(() => [...loadedData])
  const constantType = initialDataPath?.[0] || null
  const [saveError, setSaveError] = useState('')
  const [syncWarning, setSyncWarning] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [successMessageVisible, setSuccessMessageVisible] = useState(false)
  const { t } = useTranslation()
  const theme = useTheme()


  useEffect(() => {
    navigation.setOptions({
      // headerRight: () => (
      //   <HeaderIconButton
      //     onPress={() => {
      //       let newData = [...data]
      //       newData.append({id: '', text:  ''})
      //       setData(newData)
      //     }}
      //     iconName='plus-outline'
      //   />
      // ),
      title: title
    })
    checkIdExists()
  }, [])

  /**
   * ATL-815 — mint an id for every element that lacks one, and make those ids DISTINCT.
   *
   * This loop is ATL-800's root cause. It called `Date.now()` once per iteration in a synchronous
   * pass, so N id-less elements completed inside the same millisecond and every one of them
   * received the SAME id. Not a race between two user actions — a guaranteed collision for any org
   * with two or more id-less items. It is why Charge lab 2.0 had three distinct spending categories
   * (`Вода`, `Чай / кава`, `Цукор`) all carrying `spendingCategories1779208415099`: one pass of this
   * loop, one `Date.now()`, three items.
   *
   * Duplicate ids break BOTH consumers, in different ways:
   *
   *  - `migrateConstants` upserts the whole array in one statement, and Postgres refuses a statement
   *    whose proposed rows collide on the conflict key (21000). Zero rows land, so one duplicated id
   *    wiped the ENTIRE constant for that org. (ATL-799's resolver now separates them defensively,
   *    but the corrupt Firestore data is still being written, and it papers over each collision with
   *    a CONTENT-HASH suffix that moves whenever the item is edited.)
   *  - `syncEditableConstantToSupabase` builds `new Map(data.map(i => [i.id, i]))` for both the old
   *    and new arrays. A Map keyed on a duplicated id COLLAPSES those elements to one entry, so the
   *    create/update/delete diff is computed against the wrong set.
   *
   * The suffix is `${Date.now()}-${index}`: distinct within the pass by construction, and it stays
   * clear of the shape `dedupe-legacy-constant-rows-atl799.ts` treats as a deletable synthetic
   * legacy id (`/^\d+-(0?\.\d+|\d+e-\d+)$/` — timestamp, hyphen, DECIMAL). A `Math.random()` suffix
   * would match that predicate exactly and mint rows a maintenance script deletes.
   *
   * ⚠️ THE IN-PLACE MUTATION OF `element` IS LOAD-BEARING — do not "fix" it to an immutable copy.
   * `originalData` is `useState(() => [...loadedData])`, a SHALLOW copy, so it holds the same element
   * objects this loop mutates. That is the only reason `syncEditableConstantToSupabase`'s
   * `originalMap.get(item.id)` finds these items at save time. Give `data` fresh objects and leave
   * `originalData` on the old ones and every item reads as absent from the original → the diff
   * classifies the whole list as `toCreate` → a duplicate Supabase row per item on every save.
   * Cleaning this up means re-deriving `originalData` too, which is a separate change.
   */
  const checkIdExists = () => {
    const newData = [...data]
    // One clock read for the whole pass; the index is what separates the ids. See the module.
    if (assignMissingConstantIds(newData, initialDataPath ? initialDataPath[0] : '', Date.now())) {
      setData(newData)
    }
  }

  const setText = (index, text) => {
    let newData = [...data]
    let newItem = {...data[index]}
    newItem.text = text
    newData[index] = newItem
    setData(newData)
  }

  const addNewRow = () => {
    let newData = [...data]
    // ATL-815: same generator, same hazard — two rows added inside one millisecond would share an
    // id. Not reachable by hand, but this is the function that mints the ids `migrateConstants` and
    // the Supabase diff both key on, so it uses the row position for the same reason `checkIdExists`
    // does rather than relying on the clock to separate them.
    newData.push({
      id: (initialDataPath ? initialDataPath[0] : '') + Date.now() + '-' + newData.length,
      text: '',
    })
    setData(newData)
  }

  const deleteRow = (index) => {
    let newData = [...data]
    newData.splice(index, 1)
    setData(newData)
  }

  const moveRowDown = (index) => {
    if (index+1 != data.length) {
      let newData = [...data]
      newData[index+1] = data[index]
      newData[index] = data[index+1]
      setData(newData)
    }
  }

  const moveRowUp = (index) => {
    if (index != 0) {
      let newData = [...data]
      newData[index-1] = data[index]
      newData[index] = data[index-1]
      setData(newData)
    }
  }

  const save = async () => {
    let error = false
    setSaveError('')
    setSyncWarning('')
    for (let index = 0; index < data.length; index++) {
      const element = data[index];
      if (element.text.length == 0) {
        error = true
      }
    }
    if (!error) {
      setIsLoading(true)
      try {
        const result = await updateConstantsData(ref, data, constantType, originalData)
        setSuccessMessageVisible(true)

        // Optimistic update: Update the atom so UI reflects changes immediately
        // This is especially important in Supabase mode where there's no real-time subscription for constants
        if (result.firestoreSuccess && initialDataPath && initialDataPath.length >= 2) {
          setAtomData((prev) => {
            const updated = { ...prev }
            // initialDataPath is like ['discountOptions', 'data']
            const [constantType, dataKey] = initialDataPath
            if (updated[constantType]) {
              updated[constantType] = {
                ...updated[constantType],
                [dataKey]: data,
              }
            }
            return updated
          })
        }

        // Show warning if Supabase sync failed (but Firestore saved successfully)
        if (!result.supabaseSuccess && result.supabaseError) {
          setSyncWarning(result.supabaseError)
        }
      } catch (err) {
        Sentry.captureException(err)
        setSaveError(err)
      }
      setIsLoading(false)
    } else {
      setSaveError(t('notAllFieldsFilled'))
    }
  }

  const TrashIcon = (style) => (
    <Icon {...style} name='trash-outline'/>
  );
  const ArrowDownIcon = (style) => (
    <Icon {...style} name='arrow-downward-outline'/>
  );
  const ArrowUpIcon = (style) => (
    <Icon {...style} name='arrow-upward-outline'/>
  );


  const renderItem = ({item, index}) => {
    return (
      <View
        style={[styles.item, { backgroundColor: theme['background-basic-color-1'] }]}
        key={item.id ?? item.name ?? item.text}
        testID={`editable-list-row-${index}`}
      >
        <Button style={styles.button} appearance='ghost' status='info' accessoryLeft={ArrowDownIcon} onPress={() => {moveRowDown(index)}} testID={`row-${index}-move-down`}/>
        <Button style={styles.button} appearance='ghost' status='info' accessoryLeft={ArrowUpIcon} onPress={() => {moveRowUp(index)}} testID={`row-${index}-move-up`}/>
        <StyledTextInput
          value={item.text}
          onChangeText={(text) => setText(index, text)}
          style={styles.itemTitle}
          returnKeyType='done'
          autoCapitalize='sentences'
          testID={`row-${index}-input`}
        />
        <Button style={styles.button} appearance='ghost' status='danger' accessoryLeft={TrashIcon} onPress={() => {deleteRow(index)}} testID={`row-${index}-delete`}/>
      </View>
    )
  }

  const footerItem = () => {
    return (
      <View>
        <Button appearance='outline' onPress={addNewRow} style={styles.newButton}>+</Button>
        {successMessageVisible && (
          <>
            <Text status='success' style={styles.messageText}>{t('success')}</Text>
            {syncWarning !== '' && (
              <Text status='warning' style={styles.messageText}>
                ⚠️ {syncWarning}
              </Text>
            )}
            <Button onPress={() => navigation.goBack()} style={styles.submitButton}>
              {t('back')}
            </Button>
          </>
        )}
        {saveError != '' && (
          <Text style={styles.messageText} status='danger'>{saveError}</Text>
        )}
        {isLoading ? (
          <View style={styles.spinner}><Spinner /></View>
        ) : (
          !successMessageVisible && (
            <Button style={styles.submitButton} onPress={save}>{t('save')}</Button>
          )
        )}
        <View style={{height:50}}></View>
      </View>
    )
  }

  return (
    <FlatList
      data={data}
      keyExtractor={(item, index) => item.id ?? item.name ?? item.text}
      renderItem={renderItem}
      style={styles.list}
      keyboardDismissMode='on-drag'
      keyboardShouldPersistTaps='never'
      ListFooterComponent={footerItem}
      ListHeaderComponent={headerComponent}
    />
  )
}

const styles = StyleSheet.create({
  list: {
    paddingHorizontal: 20,
    paddingVertical: 10
  },
  item: {
    flexDirection: 'row',
    alignItems: 'center',
    marginVertical: 2,
    borderRadius: 5,
    height: 56,
  },
  itemTitle: {
    flex: 1
  },
  newButton: {
    marginVertical: 2,
  },
  submitButton: {
    marginTop: 2,
  },
  messageText: {
    marginTop: 2,
  },
  spinner: {
    alignItems: 'center',
    // marginBottom: 10,
  },
})
